import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

interface Kurumsal {
  kurumsalID: number;
  firmaAdi: string;
  logo?: string;
  vd?: string;
  vn?: string;
  yetkiNo?: string;
  lisansKodu?: string;
  adres?: string;
  sehir?: string;
  aktifMi: boolean;
  eklemeTarihi: string;
}

const KurumsalBilgiler: React.FC = () => {
  const { t } = useTranslation();
  const [kurumsal, setKurumsal] = useState<Kurumsal | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoHovered, setLogoHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    firmaAdi: '',
    vd: '',
    vn: '',
    yetkiNo: '',
    lisansKodu: '',
    adres: '',
    sehir: ''
  });

  useEffect(() => { fetchKurumsalBilgiler(); }, []);

  const fetchKurumsalBilgiler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Kurumsal/1');
      const data = response.data;
      setKurumsal(data);
      setFormData({
        firmaAdi: data.firmaAdi || '',
        vd: data.vd || '',
        vn: data.vn || '',
        yetkiNo: data.yetkiNo || '',
        lisansKodu: data.lisansKodu || '',
        adres: data.adres || '',
        sehir: data.sehir || ''
      });
      if (data.logo) {
        setLogoPreview(`${API_BASE_URL}/api/Kurumsal/logo/${data.kurumsalID}?t=${Date.now()}`);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        await createKurumsalBilgiler();
      } else {
        toast.error(t('msg.islemHatasi'));
      }
    } finally {
      setLoading(false);
    }
  };

  const createKurumsalBilgiler = async () => {
    try {
      await axiosInstance.post('/api/Kurumsal', { firmaAdi: 'Firma Adı', aktifMi: true });
      await fetchKurumsalBilgiler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t('msg.islemHatasi')); return; }
    if (!file.type.startsWith('image/')) { toast.error(t('msg.islemHatasi')); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!formData.firmaAdi.trim()) { toast.error(t('msg.zorunluAlanlar')); return; }
    try {
      setSaving(true);
      await axiosInstance.put('/api/Kurumsal/1', {
        kurumsalID: 1,
        firmaAdi: formData.firmaAdi,
        vd: formData.vd || null,
        vn: formData.vn || null,
        yetkiNo: formData.yetkiNo || null,
        lisansKodu: kurumsal?.lisansKodu || null,
        adres: formData.adres || null,
        sehir: formData.sehir || null,
        aktifMi: true
      });
      if (logoFile) {
        const logoFormData = new FormData();
        logoFormData.append('logo', logoFile);
        await axiosInstance.post('/api/Kurumsal/1/logo', logoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(t('msg.islemBasarili'));
      await fetchKurumsalBilgiler();
      setLogoFile(null);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await axiosInstance.delete('/api/Kurumsal/1/logo');
      setLogoPreview(null);
      setLogoFile(null);
      toast.success(t('msg.islemBasarili'));
      await fetchKurumsalBilgiler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const lastUpdate = kurumsal?.eklemeTarihi
    ? new Date(kurumsal.eklemeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  /* ─── Loading screen ─── */
  if (loading) {
    return (
      <>
        <style>{`.kb-loading{display:flex;align-items:center;justify-content:center;min-height:60vh;flex-direction:column;gap:14px;font-family:'Inter',sans-serif;color:#94a3b8}.kb-load-ring{width:40px;height:40px;border:3px solid #e2e8f0;border-top-color:#cd6906;border-radius:50%;animation:kbSpin .7s linear infinite}@keyframes kbSpin{to{transform:rotate(360deg)}}`}</style>
        <div className="kb-loading">
          <div className="kb-load-ring" />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{t('msg.yukleniyor')}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .kb-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          padding: 24px 24px 32px;
          background: #ffffff;
          min-height: unset;
        }

        /* ── Header ── */
        .kb-header { padding-bottom: 20px; margin-bottom: 24px; border-bottom: 1px solid #f3f4f6; }
        .kb-title  { font-size: 20px; font-weight: 600; color: #111827; letter-spacing: -0.2px; margin: 0 0 3px; }
        .kb-subtitle { font-size: 13px; color: #6b7280; font-weight: 400; }

        /* ── Layout grid ── */
        .kb-grid {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 22px;
          align-items: start;
        }
        @media (max-width: 900px) { .kb-grid { grid-template-columns: 1fr; } }

        /* ── Shared card ── */
        .kb-card {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }
        .kb-card-head {
          padding: 18px 24px 14px;
          border-bottom: 1.5px solid #f1f5f9;
          display: flex; align-items: center; gap: 10px;
        }
        .kb-card-head-icon {
          width: 34px; height: 34px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          background: #fff8f0; flex-shrink: 0;
        }
        .kb-card-head-icon svg { width: 16px; height: 16px; stroke: #cd6906; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }
        .kb-card-head-title { font-size: 14px; font-weight: 700; color: #0f172a; }
        .kb-card-body { padding: 24px; }

        /* ── Section groups ── */
        .kb-section { margin-bottom: 28px; }
        .kb-section:last-child { margin-bottom: 0; }
        .kb-section-label {
          font-size: 10.5px; font-weight: 700;
          letter-spacing: 0.5px;
          color: #cbd5e1; margin-bottom: 14px;
          display: flex; align-items: center; gap: 10px;
        }
        .kb-section-label::after {
          content: ''; flex: 1; height: 1px; background: #f1f5f9;
        }
        .kb-form-row   { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .kb-form-row-1 { display: grid; grid-template-columns: 1fr; gap: 16px; }

        /* ── Fields ── */
        .kb-field { display: flex; flex-direction: column; gap: 6px; }
        .kb-field label {
          font-size: 11.5px; font-weight: 600;
          letter-spacing: 0.5px; color: #64748b;
        }
        .kb-field input, .kb-field textarea {
          padding: 10px 14px;
          border: 1.5px solid #e2e8f0; border-radius: 9px;
          font-size: 14px; font-family: inherit; color: #0f172a;
          outline: none; background: #fff;
          transition: border-color 0.15s, box-shadow 0.15s;
          box-sizing: border-box; width: 100%;
        }
        .kb-field input:focus, .kb-field textarea:focus {
          border-color: #111827;
          box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
        }
        .kb-field input::placeholder, .kb-field textarea::placeholder { color: #b0bec5; }
        .kb-field textarea { resize: vertical; min-height: 90px; line-height: 1.5; }

        /* ── Logo card ── */
        .kb-logo-box {
          position: relative;
          width: 100%; aspect-ratio: 4/3;
          border-radius: 12px;
          border: 2px dashed #e2e8f0;
          overflow: hidden; cursor: pointer;
          transition: border-color 0.2s;
          background: #f8fafc;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 14px;
        }
        .kb-logo-box:hover { border-color: #cd6906; }
        .kb-logo-img { width: 100%; height: 100%; object-fit: contain; padding: 16px; }
        .kb-logo-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; color: #94a3b8; }
        .kb-logo-empty svg { width: 36px; height: 36px; stroke: #cbd5e1; stroke-width: 1.5; fill: none; }
        .kb-logo-empty span { font-size: 12.5px; font-weight: 500; }
        .kb-logo-overlay {
          position: absolute; inset: 0;
          background: rgba(15,23,42,0.55);
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
          opacity: 0; transition: opacity 0.2s;
          border-radius: 10px;
        }
        .kb-logo-box:hover .kb-logo-overlay { opacity: 1; }
        .kb-logo-overlay svg { width: 24px; height: 24px; stroke: #fff; stroke-width: 2; fill: none; }
        .kb-logo-overlay span { font-size: 12px; font-weight: 600; color: #fff; }

        .kb-logo-actions { display: flex; gap: 8px; flex-direction: column; }
        .kb-logo-btn {
          width: 100%; padding: 9px 14px; border-radius: 9px; border: none;
          font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          transition: background 0.15s, opacity 0.15s;
        }
        .kb-logo-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }
        .kb-logo-btn-upload { background: #fff8f0; color: #cd6906; }
        .kb-logo-btn-upload:hover { background: #fde5c0; }
        .kb-logo-btn-delete { background: #fff1f2; color: #e11d48; }
        .kb-logo-btn-delete:hover { background: #ffe4e6; }

        .kb-logo-hint {
          font-size: 11px; color: #94a3b8; text-align: center;
          margin-top: 10px; line-height: 1.5;
        }

        /* ── Footer bar ── */
        .kb-footer-bar {
          position: static;
          background: #fff;
          border-top: 1px solid #f3f4f6;
          padding: 20px 24px;
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          margin-top: 28px;
        }
        .kb-footer-info { display: flex; flex-direction: column; gap: 2px; }
        .kb-footer-title { font-size: 13px; font-weight: 600; color: #0f172a; }
        .kb-footer-ts { font-size: 12px; color: #94a3b8; }
        .kb-footer-ts span { color: #64748b; font-weight: 500; }
        .kb-save-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 22px; border-radius: 8px; border: none;
          background: #cd6906;
          color: #fff; font-size: 13px; font-weight: 600; font-family: inherit;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(205,105,6,0.3);
          transition: all 0.15s;
        }
        .kb-save-btn:hover:not(:disabled) { background: #a85505; box-shadow: 0 4px 12px rgba(205,105,6,0.4); }
        .kb-save-btn:disabled { opacity: 0.65; cursor: not-allowed; }
        .kb-save-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; stroke-linecap: round; stroke-linejoin: round; }
        .kb-spinner {
          width: 15px; height: 15px;
          border: 2.5px solid rgba(255,255,255,0.4);
          border-top-color: #fff; border-radius: 50%;
          animation: kbSpin .7s linear infinite;
        }
        @keyframes kbSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="kb-page">
        {/* Page header */}
        <div className="kb-header">
          <h1 className="kb-title">{t('ayarlar.kurumsal.baslik')}</h1>
          <span className="kb-subtitle">Ayarlar &rsaquo; Kurumsal Bilgiler</span>
        </div>

        <div className="kb-grid">
          {/* ── Left: Form ── */}
          <div className="kb-card">
            <div className="kb-card-head">
              <div className="kb-card-head-icon">
                <svg viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <span className="kb-card-head-title">Firma Bilgileri</span>
            </div>
            <div className="kb-card-body">

              {/* Group 1 — Genel */}
              <div className="kb-section">
                <div className="kb-section-label">Genel Bilgiler</div>
                <div className="kb-form-row">
                  <div className="kb-field">
                    <label>Firma Adı <span style={{ color: '#e11d48' }}>*</span></label>
                    <input
                      type="text"
                      placeholder={t('label.ara')}
                      value={formData.firmaAdi}
                      onChange={e => handleInputChange('firmaAdi', e.target.value)}
                    />
                  </div>
                  <div className="kb-field">
                    <label>Şehir</label>
                    <input
                      type="text"
                      placeholder={t('label.ara')}
                      value={formData.sehir}
                      onChange={e => handleInputChange('sehir', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Group 2 — Resmi */}
              <div className="kb-section">
                <div className="kb-section-label">Vergi Bilgileri</div>
                <div className="kb-form-row">
                  <div className="kb-field">
                    <label>{t('label.vergiDairesi')}</label>
                    <input
                      type="text"
                      placeholder={t('label.ara')}
                      value={formData.vd}
                      onChange={e => handleInputChange('vd', e.target.value)}
                    />
                  </div>
                  <div className="kb-field">
                    <label>Vergi Numarası</label>
                    <input
                      type="text"
                      placeholder="1234567890"
                      value={formData.vn}
                      onChange={e => handleInputChange('vn', e.target.value)}
                    />
                  </div>
                </div>
                <div className="kb-form-row" style={{ marginTop: 16 }}>
                  <div className="kb-field">
                    <label>Yetki No</label>
                    <input
                      type="text"
                      placeholder={t('label.ara')}
                      value={formData.yetkiNo}
                      onChange={e => handleInputChange('yetkiNo', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Group 3 — Lisans */}
              <div className="kb-section">
                <div className="kb-section-label">Lisans</div>
                <div className="kb-form-row-1">
                  <div className="kb-field">
                    <label>Lisans Kodu</label>
                    <input
                      type="text"
                      readOnly
                      value={formData.lisansKodu || '—'}
                      style={{ fontFamily: 'monospace', letterSpacing: '0.5px', background: '#f8fafc', color: '#64748b', cursor: 'default' }}
                    />
                  </div>
                </div>
              </div>

              {/* Group 4 — Adres */}
              <div className="kb-section">
                <div className="kb-section-label">Konum</div>
                <div className="kb-form-row-1">
                  <div className="kb-field">
                    <label>{t('label.adres')}</label>
                    <textarea
                      placeholder="Tam firma adresi..."
                      value={formData.adres}
                      onChange={e => handleInputChange('adres', e.target.value)}
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* ── Right: Logo ── */}
          <div className="kb-card">
            <div className="kb-card-head">
              <div className="kb-card-head-icon">
                <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              <span className="kb-card-head-title">Marka Kimliği</span>
            </div>
            <div className="kb-card-body">

              {/* Logo preview box */}
              <div
                className="kb-logo-box"
                onMouseEnter={() => setLogoHovered(true)}
                onMouseLeave={() => setLogoHovered(false)}
                onClick={() => fileInputRef.current?.click()}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Firma logosu" className="kb-logo-img" />
                ) : (
                  <div className="kb-logo-empty">
                    <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    <span>Logo yüklenmedi</span>
                  </div>
                )}
                <div className="kb-logo-overlay">
                  <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span>{logoPreview ? 'Logo Değiştir' : 'Logo Yükle'}</span>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoChange}
              />

              <div className="kb-logo-actions">
                <button
                  type="button"
                  className="kb-logo-btn kb-logo-btn-upload"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {logoPreview ? 'Logo Değiştir' : 'Logo Yükle'}
                </button>
                {logoPreview && (
                  <button
                    type="button"
                    className="kb-logo-btn kb-logo-btn-delete"
                    onClick={handleLogoDelete}
                  >
                    <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    Logoyu Kaldır
                  </button>
                )}
              </div>

              <p className="kb-logo-hint">
                PNG, JPG veya GIF · Maks. 5 MB<br />
                Önerilen boyut: 400 × 200 px
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky footer action bar ── */}
      <div className="kb-footer-bar">
        <div className="kb-footer-info">
          <span className="kb-footer-title">{t('ayarlar.kurumsal.baslik')}</span>
          {lastUpdate && (
            <span className="kb-footer-ts">Son güncelleme: <span>{lastUpdate}</span></span>
          )}
        </div>
        <button className="kb-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? (
            <span className="kb-spinner" />
          ) : (
            <svg viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          )}
          {saving ? 'Kaydediliyor...' : 'Güncelle'}
        </button>
      </div>
    </>
  );
};

export default KurumsalBilgiler;